import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { BookOpen, Volume2, X, Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const GrammarExplanationModal = ({ 
  isOpen, 
  onClose, 
  explanation, 
  selectedText,
  onAddToTraining 
}) => {
  const { toast } = useToast();

  if (!explanation) return null;

  const handleAddToTraining = () => {
    if (onAddToTraining) {
      onAddToTraining({
        type: 'grammar_explanation',
        title: explanation.title,
        text: selectedText,
        explanation: explanation.explanation,
        level: explanation.level
      });
    }
    
    toast({
      title: "Grammatik hinzugefügt",
      description: `"${explanation.title}" wurde zum Training hinzugefügt.`,
    });
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800',
      'A2': 'bg-blue-100 text-blue-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-orange-100 text-orange-800',
      'C1': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-5 w-5 text-[#4ECDC4]" />
              <DialogTitle className="text-xl font-bold">
                {explanation.title}
              </DialogTitle>
            </div>
            <div className="flex items-center space-x-2">
              <Badge className={getLevelColor(explanation.level)}>
                {explanation.level}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Selected Text */}
          {selectedText && (
            <Card className="bg-[#4ECDC4] bg-opacity-10 border-[#4ECDC4]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Ausgewählter Text:</h4>
                    <p className="text-lg font-semibold text-[#4ECDC4]">
                      "{selectedText}"
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => speakText(selectedText)}
                  >
                    <Volume2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Explanation */}
          <div>
            <h4 className="font-medium text-slate-900 mb-3">Erklärung:</h4>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-slate-800 leading-relaxed">
                {explanation.explanation}
              </p>
            </div>
          </div>

          {/* Examples */}
          {explanation.examples && explanation.examples.length > 0 && (
            <div>
              <h4 className="font-medium text-slate-900 mb-3">Beispiele:</h4>
              <div className="space-y-3">
                {explanation.examples.map((example, index) => (
                  <Card key={index} className="border-l-4 border-[#4ECDC4]">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-slate-800">
                          {example}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => speakText(example)}
                        >
                          <Volume2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Usage Tips */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">💡 Tipp:</h4>
            <p className="text-blue-800 text-sm">
              Klicken Sie auf weitere Textstellen in der Geschichte, um ähnliche Grammatikstrukturen zu entdecken.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button
              className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white flex-1"
              onClick={handleAddToTraining}
            >
              <Plus className="h-4 w-4 mr-2" />
              Zum Training hinzufügen
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Schließen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GrammarExplanationModal;